import React, { useEffect, useState } from 'react';
import { Grid, Layers, ChevronLeft, ChevronRight, Info, X, Diamond, Heart, Gift, Star, BookOpen, Check, Lock } from 'lucide-react';
import { SkinCard } from './SkinCard';
import { SkinCarousel } from './SkinCarousel';
import { SkinPreviewModal } from './SkinPreviewModal';
import { MaschaHeroBanner } from './MaschaHeroBanner';
import { MaschaSkinsCarousel } from './MaschaSkinsCarousel';
import { TeasePreviewModal } from './TeasePreviewModal';
import { SpinTheWheelModal } from './SpinTheWheelModal';
import { MaschaStorylineModal } from './MaschaStorylineModal';
import { MaschaSkinsData, MaschaBondBonus } from './MaschaSkinsData';
// Angel skin data structure
export interface AngelSkin {
  id?: string;
  angel_name: string;
  skin_name: string;
  visual_theme: string;
  personality_tone: string;
  currency_type: 'Love Tokens' | 'Desire Gems';
  price: number;
  unlock_method: string;
  status: 'Locked' | 'Unlocked' | 'Active';
  rarity: 'Common' | 'Rare' | 'Epic' | 'Legendary';
  time_limited: boolean;
  countdown: string | null;
  fomo_label: string | null;
  preview_image_url: string;
  short_description: string;
}
// Sample angels data
const angelsData: AngelSkin[] = [{
  id: 'jade-dark',
  angel_name: 'Jade',
  skin_name: 'Dark Fantasy Jade',
  visual_theme: 'Gothic succubus with black-red dress, horns, and smoky castle backdrop',
  personality_tone: 'Dominant, mysterious, poetic flirtation',
  currency_type: 'Love Tokens',
  price: 20,
  unlock_method: 'Direct purchase',
  status: 'Locked',
  rarity: 'Epic',
  time_limited: true,
  countdown: '23:59:12',
  fomo_label: 'Limited Halloween Drop',
  preview_image_url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/2ZumR5LYPYmPJKDgeAP8f9/1.jpg',
  short_description: "Jade's dark side awakens in this forbidden castle fantasy."
}, {
  id: 'jade-cyber',
  angel_name: 'Jade',
  skin_name: 'CyberDomme Jade',
  visual_theme: 'Futuristic cyberpunk dominatrix with neon lights and latex',
  personality_tone: 'High-tech arrogant, commanding presence',
  currency_type: 'Desire Gems',
  price: 45,
  unlock_method: 'Quest reward – Complete the Cyber Challenge',
  status: 'Locked',
  rarity: 'Legendary',
  time_limited: false,
  countdown: null,
  fomo_label: null,
  preview_image_url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/vtNw24U74n2kbAswZ4s9fF/5.jpg',
  short_description: "Prove your worth to Jade's cybernetic alter ego."
}, {
  id: 'summer-tropical',
  angel_name: 'Summer',
  skin_name: 'Tropical Summer',
  visual_theme: 'Bikini, palm trees, sunny beach background',
  personality_tone: 'Playful, flirty, full of double meanings',
  currency_type: 'Desire Gems',
  price: 50,
  unlock_method: 'Limited Summer Event',
  status: 'Locked',
  rarity: 'Legendary',
  time_limited: true,
  countdown: '48:00:00',
  fomo_label: 'Event Exclusive',
  preview_image_url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/hQWzTVsozeh8Kz68cSdBMu/4.jpg',
  short_description: 'Your beach fantasy brought to life for a limited time.'
}, {
  id: 'summer-cosplay',
  angel_name: 'Summer',
  skin_name: 'Cosplay Mode',
  visual_theme: 'Anime/game cosplay outfits with vibrant colors',
  personality_tone: 'Nerdy, enthusiastic, pop-culture references',
  currency_type: 'Love Tokens',
  price: 0,
  unlock_method: 'Reach relationship level 5',
  status: 'Locked',
  rarity: 'Rare',
  time_limited: false,
  countdown: null,
  fomo_label: null,
  preview_image_url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jXJFcbgwSeXsSaQYxjo4wA/2.jpg',
  short_description: 'Summer plays your favorite game characters in this playful mode.'
}, {
  id: 'luna-starlight',
  angel_name: 'Luna',
  skin_name: 'Starlight Sorceress Luna',
  visual_theme: 'Mystical robes with glowing constellations',
  personality_tone: 'Wise, enchanting, slightly mischievous',
  currency_type: 'Desire Gems',
  price: 60,
  unlock_method: 'Event Quest – Celestial Festival',
  status: 'Locked',
  rarity: 'Legendary',
  time_limited: true,
  countdown: '72:00:00',
  fomo_label: 'Festival Exclusive',
  preview_image_url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4aqBYWSqi1HYJLKaw3ePUW/3.jpg',
  short_description: 'Luna channels the cosmos to cast a spell on you.'
}, {
  id: 'luna-winter',
  angel_name: 'Luna',
  skin_name: 'Winter Empress Luna',
  visual_theme: 'White fur coat, icy crown, snowflake background',
  personality_tone: 'Elegant, cool, regal',
  currency_type: 'Love Tokens',
  price: 25,
  unlock_method: 'Direct purchase – Winter Sale',
  status: 'Locked',
  rarity: 'Epic',
  time_limited: true,
  countdown: '120:00:00',
  fomo_label: 'Seasonal Sale',
  preview_image_url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jXJFcbgwSeXsSaQYxjo4wA/2.jpg',
  short_description: "Rule the winter nights with Luna's icy elegance."
}];
// Get unique angel names
const getUniqueAngels = (skins: AngelSkin[]): string[] => {
  return [...new Set(skins.map(skin => skin.angel_name))];
};
export const AngelSkinsShop: React.FC = () => {
  const [viewMode, setViewMode] = useState<'grid' | 'carousel'>('grid');
  const [selectedAngel, setSelectedAngel] = useState<string | null>(null);
  const [selectedSkin, setSelectedSkin] = useState<AngelSkin | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [currency, setCurrency] = useState({
    loveTokens: 45,
    desireGems: 120
  });
  const [skins, setSkins] = useState<AngelSkin[]>([...angelsData]);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // Mascha-specific states
  const [showMaschaSection, setShowMaschaSection] = useState(true);
  const [unlockedMaschaSkins, setUnlockedMaschaSkins] = useState<string[]>(['mascha-nurse']); // First skin is free
  const [showTeasePreview, setShowTeasePreview] = useState(false);
  const [selectedMaschaSkinId, setSelectedMaschaSkinId] = useState('');
  const [showSpinWheel, setShowSpinWheel] = useState(false);
  const [spinsAvailable, setSpinsAvailable] = useState(1);
  const [showStoryline, setShowStoryline] = useState(false);
  const [showBondBonus, setShowBondBonus] = useState(false);
  const uniqueAngels = getUniqueAngels(skins);
  // Add Mascha to the list of unique angels
  const allAngels = ['Mascha', ...uniqueAngels];
  // Initialize with first angel selected
  useEffect(() => {
    if (allAngels.length > 0 && !selectedAngel) {
      setSelectedAngel('Mascha'); // Default to Mascha
    }
  }, [allAngels, selectedAngel]);
  // Update countdowns for time-limited skins
  useEffect(() => {
    const timer = setInterval(() => {
      setSkins(prevSkins => prevSkins.map(skin => {
        if (!skin.time_limited || !skin.countdown) return skin;
        // Parse the countdown string (HH:MM:SS)
        const [hours, minutes, seconds] = skin.countdown.split(':').map(Number);
        // Calculate total seconds
        let totalSeconds = hours * 3600 + minutes * 60 + seconds;
        // Decrease by 1 second
        totalSeconds -= 1;
        if (totalSeconds <= 0) {
          // Time's up!
          return {
            ...skin,
            time_limited: false,
            countdown: null,
            fomo_label: 'Expired'
          };
        }
        // Convert back to HH:MM:SS format
        const newHours = Math.floor(totalSeconds / 3600);
        const newMinutes = Math.floor(totalSeconds % 3600 / 60);
        const newSeconds = totalSeconds % 60;
        const newCountdown = `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}:${String(newSeconds).padStart(2, '0')}`;
        return {
          ...skin,
          countdown: newCountdown
        };
      }));
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  // Filter skins by selected angel
  const filteredSkins = selectedAngel ? skins.filter(skin => skin.angel_name === selectedAngel) : skins;
  const handleSkinClick = (skin: AngelSkin) => {
    setSelectedSkin(skin);
    setShowModal(true);
  };
  const handleUnlock = (skin: AngelSkin) => {
    if (skin.status === 'Locked') {
      if (skin.currency_type === 'Love Tokens' && currency.loveTokens >= skin.price) {
        // Update currency
        setCurrency({
          ...currency,
          loveTokens: currency.loveTokens - skin.price
        });
        // Update skin status
        setSkins(prevSkins => prevSkins.map(s => s.angel_name === skin.angel_name && s.skin_name === skin.skin_name ? {
          ...s,
          status: 'Unlocked'
        } : s));
        // Show success message
        setSuccessMessage(`Successfully unlocked ${skin.skin_name}!`);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        // Close modal if open
        setShowModal(false);
      } else if (skin.currency_type === 'Desire Gems' && currency.desireGems >= skin.price) {
        // Update currency
        setCurrency({
          ...currency,
          desireGems: currency.desireGems - skin.price
        });
        // Update skin status
        setSkins(prevSkins => prevSkins.map(s => s.angel_name === skin.angel_name && s.skin_name === skin.skin_name ? {
          ...s,
          status: 'Unlocked'
        } : s));
        // Show success message
        setSuccessMessage(`Successfully unlocked ${skin.skin_name}!`);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        // Close modal if open
        setShowModal(false);
      } else {
        // Show insufficient funds message
        setSuccessMessage(`Insufficient ${skin.currency_type} to unlock this skin!`);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
      }
    } else if (skin.status === 'Unlocked') {
      // Activate the skin
      setSkins(prevSkins => {
        // First set all skins for this angel to not active
        const updatedSkins = prevSkins.map(s => s.angel_name === skin.angel_name && s.status === 'Active' ? {
          ...s,
          status: 'Unlocked'
        } : s);
        // Then set the selected skin to active
        return updatedSkins.map(s => s.angel_name === skin.angel_name && s.skin_name === skin.skin_name ? {
          ...s,
          status: 'Active'
        } : s);
      });
      // Show success message
      setSuccessMessage(`${skin.skin_name} is now active!`);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      // Close modal if open
      setShowModal(false);
    }
  };
  // Handle Mascha skin unlock
  const handleMaschaSkinUnlock = (skinId: string) => {
    const skin = MaschaSkinsData.find(s => s.id === skinId);
    if (!skin || unlockedMaschaSkins.includes(skinId)) return;
    if (skin.currency_type === 'Love Tokens' && currency.loveTokens >= skin.price || skin.currency_type === 'Desire Gems' && currency.desireGems >= skin.price) {
      // Update currency
      setCurrency(prev => ({
        loveTokens: skin.currency_type === 'Love Tokens' ? prev.loveTokens - skin.price : prev.loveTokens,
        desireGems: skin.currency_type === 'Desire Gems' ? prev.desireGems - skin.price : prev.desireGems
      }));
      // Add to unlocked skins
      setUnlockedMaschaSkins(prev => [...prev, skinId]);
      // Show success message
      setSuccessMessage(`Successfully unlocked ${skin.skin_name}!`);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      // Close tease preview if open
      setShowTeasePreview(false);
      // Show storyline if this completes a chapter
      if (unlockedMaschaSkins.length + 1 === MaschaSkinsData.length) {
        setTimeout(() => {
          setShowBondBonus(true);
        }, 1000);
      } else {
        setTimeout(() => {
          setShowStoryline(true);
        }, 1000);
      }
    } else {
      // Show insufficient funds message
      setSuccessMessage(`Insufficient ${skin.currency_type} to unlock this skin!`);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    }
  };
  // Handle spin wheel reward
  const handleSpinReward = (reward: any) => {
    if (reward.type === 'skin') {
      // Unlock the skin
      setUnlockedMaschaSkins(prev => prev.includes(reward.skinId) ? prev : [...prev, reward.skinId]);
      setSuccessMessage(`You won the ${reward.name}!`);
    } else if (reward.type === 'tokens') {
      // Add tokens
      setCurrency(prev => ({
        ...prev,
        loveTokens: prev.loveTokens + reward.amount
      }));
      setSuccessMessage(`You won ${reward.amount} Love Tokens!`);
    } else if (reward.type === 'gems') {
      // Add gems
      setCurrency(prev => ({
        ...prev,
        desireGems: prev.desireGems + reward.amount
      }));
      setSuccessMessage(`You won ${reward.amount} Desire Gems!`);
    } else if (reward.type === 'tease') {
      // Enable a special tease preview
      setSuccessMessage(`You won a special Tease Preview!`);
      setTimeout(() => {
        setShowSpinWheel(false);
        setSelectedMaschaSkinId(MaschaSkinsData[2].id); // BD-inspired Fashion skin
        setShowTeasePreview(true);
      }, 500);
      return;
    }
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
    setShowSpinWheel(false);
  };
  return <div className="w-full min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-white" data-id="element-1757">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-md" data-id="element-1758">
        <div className="container mx-auto px-4 py-4" data-id="element-1759">
          <div className="flex items-center justify-between" data-id="element-1760">
            <h1 className="text-xl font-bold flex items-center" data-id="element-1761">
              <Layers className="mr-2 text-pink-500" data-id="element-1762" />
              Angel Skins Shop
            </h1>
            <div className="flex items-center space-x-4" data-id="element-1763">
              {/* Currency display */}
              <div className="flex items-center space-x-3" data-id="element-1764">
                <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-full px-3 py-1" data-id="element-1765">
                  <Heart size={16} className="text-pink-500 mr-1" data-id="element-1766" />
                  <span data-id="element-1767">{currency.loveTokens}</span>
                </div>
                <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-full px-3 py-1" data-id="element-1768">
                  <Diamond size={16} className="text-blue-400 mr-1" data-id="element-1769" />
                  <span data-id="element-1770">{currency.desireGems}</span>
                </div>
              </div>
              {/* View toggle */}
              <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-1 flex" data-id="element-1771">
                <button className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-gray-200 dark:bg-gray-700' : ''}`} onClick={() => setViewMode('grid')} data-id="element-1772">
                  <Grid size={18} data-id="element-1773" />
                </button>
                <button className={`p-1.5 rounded ${viewMode === 'carousel' ? 'bg-gray-200 dark:bg-gray-700' : ''}`} onClick={() => setViewMode('carousel')} data-id="element-1774">
                  <Layers size={18} data-id="element-1775" />
                </button>
              </div>
            </div>
          </div>
          {/* Angel selection tabs */}
          <div className="flex overflow-x-auto scrollbar-hide mt-4 pb-2" data-id="element-1776">
            {allAngels.map(angel => <button key={angel} className={`px-4 py-2 mx-1 rounded-full whitespace-nowrap transition-colors ${selectedAngel === angel ? angel === 'Mascha' ? 'bg-gradient-to-r from-amber-600 to-red-600 text-white' : 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700'}`} onClick={() => {
            setSelectedAngel(angel);
            if (angel === 'Mascha') {
              setShowMaschaSection(true);
            } else {
              setShowMaschaSection(false);
            }
          }} data-id="element-1777">
                {angel === 'Mascha' && <span className="inline-block w-4 h-4 mr-1 rounded-full bg-gradient-to-r from-red-500 to-amber-500" data-id="element-1778"></span>}
                {angel}
                {angel === 'Mascha' && <span className="ml-1 text-xs bg-white/30 px-1.5 py-0.5 rounded-full" data-id="element-1779">
                    NEW
                  </span>}
              </button>)}
            <button className="px-4 py-2 mx-1 rounded-full whitespace-nowrap bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700" onClick={() => {
            setSelectedAngel(null);
            setShowMaschaSection(false);
          }} data-id="element-1780">
              All Angels
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 py-6" data-id="element-1781">
        {/* Mascha section */}
        {showMaschaSection && <div className="mb-8" data-id="element-1782">
            {/* Luxurious background for Mascha section */}
            <div className="bg-white dark:bg-gradient-to-b dark:from-gray-900 dark:to-black rounded-xl p-6 border border-gray-200 dark:border-amber-800/30 shadow-xl" data-id="element-1783">
              {/* Hero banner */}
              <MaschaHeroBanner onTeasePreview={skinId => {
            setSelectedMaschaSkinId(skinId);
            setShowTeasePreview(true);
          }} onSpinWheel={() => setShowSpinWheel(true)} userTokens={currency} data-id="element-1784" />
              {/* Mascha skins carousel */}
              <MaschaSkinsCarousel onSkinSelect={skinId => {
            setSelectedMaschaSkinId(skinId);
            setShowTeasePreview(true);
          }} unlockedSkins={unlockedMaschaSkins} data-id="element-1785" />
              {/* Mascha storyline and bond progress */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1786">
                <div className="bg-white/80 dark:bg-black/40 rounded-xl p-4 border border-gray-200 dark:border-amber-800/30" data-id="element-1787">
                  <div className="flex items-center justify-between mb-3" data-id="element-1788">
                    <h3 className="text-gray-800 dark:text-amber-400 font-bold flex items-center" data-id="element-1789">
                      <BookOpen size={18} className="mr-2" data-id="element-1790" />
                      Mascha's Storyline
                    </h3>
                    <button onClick={() => setShowStoryline(true)} className="px-3 py-1 bg-gradient-to-r from-amber-600 to-red-600 rounded-lg text-white text-xs hover:shadow-lg hover:shadow-amber-500/20 transition-all duration-300" disabled={unlockedMaschaSkins.length <= 0} data-id="element-1791">
                      Read Story
                    </button>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-3" data-id="element-1792">
                    Unlock all of Mascha's skins to experience her complete
                    intimate storyline.
                  </p>
                  <div className="space-y-2" data-id="element-1793">
                    {MaschaSkinsData.map((skin, index) => <div key={skin.id} className="flex items-center" data-id="element-1794">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 ${unlockedMaschaSkins.includes(skin.id) ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`} data-id="element-1795">
                          {unlockedMaschaSkins.includes(skin.id) ? <Check size={14} data-id="element-1796" /> : <Lock size={14} data-id="element-1797" />}
                        </div>
                        <div className="flex-grow" data-id="element-1798">
                          <div className="flex items-center justify-between" data-id="element-1799">
                            <span className={`text-sm ${unlockedMaschaSkins.includes(skin.id) ? 'text-gray-800 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-1800">
                              Chapter {index + 1}: {skin.skin_name}
                            </span>
                            {!unlockedMaschaSkins.includes(skin.id) && <span className="text-xs text-gray-500" data-id="element-1801">
                                Locked
                              </span>}
                          </div>
                          <div className="h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mt-1" data-id="element-1802">
                            <div className={`h-full ${unlockedMaschaSkins.includes(skin.id) ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gray-200 dark:bg-gray-700'}`} style={{
                        width: unlockedMaschaSkins.includes(skin.id) ? '100%' : '0%'
                      }} data-id="element-1803"></div>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>
                <div className="bg-white/80 dark:bg-black/40 rounded-xl p-4 border border-gray-200 dark:border-amber-800/30" data-id="element-1804">
                  <h3 className="text-gray-800 dark:text-amber-400 font-bold flex items-center mb-3" data-id="element-1805">
                    <Gift size={18} className="mr-2" data-id="element-1806" />
                    Bond Bonus Reward
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-3" data-id="element-1807">
                    Unlock all 5 of Mascha's skins to receive an exclusive Bond
                    Bonus package.
                  </p>
                  <div className="space-y-3" data-id="element-1808">
                    {MaschaBondBonus.rewards.map((reward, index) => <div key={index} className="flex items-start" data-id="element-1809">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${unlockedMaschaSkins.length === MaschaSkinsData.length ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white' : 'bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-500'}`} data-id="element-1810">
                          {index === 0 && <Star size={16} fill={unlockedMaschaSkins.length === MaschaSkinsData.length ? 'currentColor' : 'none'} data-id="element-1811" />}
                          {index === 1 && <Heart size={16} fill={unlockedMaschaSkins.length === MaschaSkinsData.length ? 'currentColor' : 'none'} data-id="element-1812" />}
                          {index === 2 && <Diamond size={16} fill={unlockedMaschaSkins.length === MaschaSkinsData.length ? 'currentColor' : 'none'} data-id="element-1813" />}
                        </div>
                        <div data-id="element-1814">
                          <h4 className={`font-medium ${unlockedMaschaSkins.length === MaschaSkinsData.length ? 'text-gray-800 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`} data-id="element-1815">
                            {reward.name}
                          </h4>
                          <p className={`text-xs ${unlockedMaschaSkins.length === MaschaSkinsData.length ? 'text-gray-600 dark:text-gray-300' : 'text-gray-500 dark:text-gray-500'}`} data-id="element-1816">
                            {reward.description}
                          </p>
                        </div>
                      </div>)}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-1817">
                    <div className="flex items-center justify-between" data-id="element-1818">
                      <span className="text-gray-600 dark:text-gray-400 text-sm" data-id="element-1819">
                        Unlock Progress
                      </span>
                      <span className="text-gray-700 dark:text-amber-400 text-sm font-medium" data-id="element-1820">
                        {unlockedMaschaSkins.length}/{MaschaSkinsData.length}{' '}
                        Skins
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mt-2" data-id="element-1821">
                      <div className="h-full bg-gradient-to-r from-amber-500 to-yellow-500 transition-all duration-500" style={{
                    width: `${unlockedMaschaSkins.length / MaschaSkinsData.length * 100}%`
                  }} data-id="element-1822"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>}

        {/* Regular angel skins content */}
        {(!showMaschaSection || !selectedAngel) && (viewMode === 'grid' ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1823">
              {filteredSkins.map((skin, index) => <SkinCard key={`${skin.angel_name}-${skin.skin_name}-${index}`} skin={skin} onClick={() => handleSkinClick(skin)} onUnlock={() => handleUnlock(skin)} data-id="element-1824" />)}
            </div> : <SkinCarousel skins={filteredSkins} onSkinClick={handleSkinClick} onUnlock={handleUnlock} data-id="element-1825" />)}
      </div>

      {/* Skin preview modal */}
      {showModal && selectedSkin && <SkinPreviewModal skin={skins.find(s => s.angel_name === selectedSkin.angel_name && s.skin_name === selectedSkin.skin_name) || selectedSkin} onClose={() => setShowModal(false)} onUnlock={() => handleUnlock(selectedSkin)} data-id="element-1826" />}

      {/* Mascha Tease Preview Modal */}
      <TeasePreviewModal isOpen={showTeasePreview} onClose={() => setShowTeasePreview(false)} skinId={selectedMaschaSkinId} userTokens={currency} onUnlock={handleMaschaSkinUnlock} data-id="element-1827" />

      {/* Spin The Wheel Modal */}
      <SpinTheWheelModal isOpen={showSpinWheel} onClose={() => setShowSpinWheel(false)} onRewardClaimed={handleSpinReward} spinsAvailable={spinsAvailable} onSpinUsed={() => setSpinsAvailable(prev => Math.max(0, prev - 1))} data-id="element-1828" />

      {/* Mascha Storyline Modal */}
      <MaschaStorylineModal isOpen={showStoryline} onClose={() => setShowStoryline(false)} unlockedSkins={unlockedMaschaSkins} data-id="element-1829" />

      {/* Bond Bonus Modal */}
      {showBondBonus && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-1830">
          <div className="relative w-full max-w-lg rounded-xl overflow-hidden bg-gradient-to-b from-amber-900/50 to-black border border-amber-500/30 shadow-xl animate-in zoom-in-95 duration-300" data-id="element-1831">
            <button className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors" onClick={() => setShowBondBonus(false)} data-id="element-1832">
              <X size={18} data-id="element-1833" />
            </button>
            <div className="p-6" data-id="element-1834">
              <div className="flex flex-col items-center text-center mb-6" data-id="element-1835">
                <div className="w-20 h-20 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 flex items-center justify-center mb-4" data-id="element-1836">
                  <Star size={32} className="text-white" fill="currentColor" data-id="element-1837" />
                </div>
                <h2 className="text-xl font-bold text-amber-400 mb-2" data-id="element-1838">
                  {MaschaBondBonus.title}
                </h2>
                <p className="text-gray-300 text-sm" data-id="element-1839">
                  {MaschaBondBonus.description}
                </p>
              </div>
              <div className="space-y-4 mb-6" data-id="element-1840">
                {MaschaBondBonus.rewards.map((reward, index) => <div key={index} className="bg-black/40 rounded-lg p-4 border border-amber-800/30 flex items-center gap-3" data-id="element-1841">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-600 to-red-600 flex items-center justify-center flex-shrink-0" data-id="element-1842">
                      {index === 0 && <Star size={18} className="text-white" fill="currentColor" data-id="element-1843" />}
                      {index === 1 && <Heart size={18} className="text-white" fill="currentColor" data-id="element-1844" />}
                      {index === 2 && <Diamond size={18} className="text-white" fill="currentColor" data-id="element-1845" />}
                    </div>
                    <div data-id="element-1846">
                      <h3 className="font-medium text-white" data-id="element-1847">{reward.name}</h3>
                      <p className="text-gray-300 text-sm" data-id="element-1848">
                        {reward.description}
                      </p>
                    </div>
                  </div>)}
              </div>
              <button onClick={() => setShowBondBonus(false)} className="w-full py-3 bg-gradient-to-r from-amber-600 to-red-600 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-amber-500/20 transition-all duration-300" data-id="element-1849">
                Claim Rewards
              </button>
            </div>
          </div>
        </div>}

      {/* Success/Error Message Toast */}
      {showSuccessMessage && <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-in fade-in slide-in-from-bottom-5 duration-300" data-id="element-1850">
          {successMessage}
        </div>}

      {/* Scrollbar hide style */}
      <style jsx data-id="element-1851">{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes pulse-subtle {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
        }
        .animate-pulse-subtle {
          animation: pulse-subtle 2s infinite;
        }
      `}</style>
    </div>;
};